import React from 'react';
import { Box, Typography, Switch, TextField, InputAdornment, ButtonBase } from '@mui/material';
import { WEEKDAY_SHORT } from '../utils/schedule';

/**
 * "Different price on some days?" — one switch, then a small price box per day.
 * Shared by Add Product and the Price dialog.
 *
 * value:     { "0": "8", "6": "" ... } — empty object means the switch is off (same price every day).
 *            Day keys are 0=Monday..6=Sunday, values are strings while typing.
 * onChange:  called with the next object
 * basePrice: shown as the hint in each empty box, so "empty = normal price" needs no words
 *
 * All seven days always show, even on a product delivered only on some days: plans made from
 * this product ("Wednesday Sakshi") take their price from it and can run on any day.
 */
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

/** The typed day prices as numbers, empty boxes dropped — ready to send. */
export const cleanDayPrices = (value) => {
    return Object.fromEntries(
        Object.entries(value || {})
            .filter(([, v]) => v !== '' && v != null && !isNaN(parseFloat(v)))
            .map(([d, v]) => [d, parseFloat(v)])
    );
};

const DayPricesEditor = ({ value, onChange, basePrice }) => {
    const days = ALL_DAYS;

    const on = Object.keys(value || {}).length > 0;
    const toggle = () => onChange(on ? {} : Object.fromEntries(days.map((d) => [String(d), ''])));
    const base = basePrice !== '' && basePrice != null && !isNaN(parseFloat(basePrice)) ? String(parseFloat(basePrice)) : '';

    return (
        <Box sx={{ border: '1px solid', borderColor: on ? 'primary.light' : 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {/* The whole row is the switch — a 44px+ target, not just the small knob. */}
            <ButtonBase onClick={toggle} sx={{ width: '100%', minHeight: 52, px: 1.5, justifyContent: 'space-between', textAlign: 'left',
                                                 bgcolor: on ? 'rgba(25,118,210,0.06)' : 'transparent' }}>
                <Box sx={{ minWidth: 0, pr: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Different price on some days?</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {on ? 'Leave a day empty to use the normal price' : 'e.g. Sunday paper costs more'}
                    </Typography>
                </Box>
                <Switch checked={on} tabIndex={-1} sx={{ pointerEvents: 'none', flexShrink: 0 }} />
            </ButtonBase>

            {on && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 1.5, pb: 1.5, pt: 0.5 }}>
                    {days.map((d) => (
                        <TextField key={d} size="small" type="number" label={WEEKDAY_SHORT[d]}
                            value={value?.[d] ?? value?.[String(d)] ?? ''}
                            placeholder={base}
                            onChange={(e) => onChange({ ...value, [String(d)]: e.target.value })}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            inputProps={{ min: 0, step: 0.5, inputMode: 'decimal' }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 90 }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default DayPricesEditor;
